import React from "react";
import { FieldArray, getIn } from "formik";
import { makeStyles } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import FormLabel from "@material-ui/core/FormLabel";
import Paper from "@material-ui/core/Paper";
import CloseIcon from "@material-ui/icons/Close";
import Details from "../../common/Details";
import FormSection from "../../common/FormSection";
import FormStep from "../../common/FormStep";
import InputField from "../../common/InputField";
import FileField from "../../common/FileField";
import PleaseExplain from "../../common/PleaseExplain";
import TextareaWithCharCount from "../../common/TextareaWithCharCount";
import YesNoField from "../../common/YesNoField";

// for array field
const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(4),
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(0.5),
    top: theme.spacing(0.5),
    zIndex: 200,
  },
  paper: {
    position: "relative",
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),

    "& .MuiFormControl-root": {
      marginBottom: 0,
      marginTop: 0,
      "&:not(:last-child)": {
        marginBottom: theme.spacing(2),
      },
    },

    "& .MuiInputBase-root": {
      minWidth: 250,
    },
  },
}));

const SecurityClearancesFields = ({ form, push, remove }: any) => {
  const classes = useStyles();
  const values = getIn(form.values, "securityClearances");

  return (
    <div className={classes.root}>
      <FormLabel>2. Identify Agency</FormLabel>
      {values.map((_value: string, index: number) => (
        <Paper key={index} className={classes.paper} elevation={3}>
          <IconButton
            className={classes.closeButton}
            onClick={() => {
              remove(index);
              if (values.length <= 1) {
                push({
                  federalAgency: "",
                  clearanceLevel: "",
                  dateOfApproval: "",
                });
              }
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
          <InputField
            label="Federal agency"
            name={`securityClearances[${index}].federalAgency`}
            required={true}
          />
          <InputField
            label="Clearance level"
            name={`securityClearances[${index}].clearanceLevel`}
            required={true}
          />
          <InputField
            label="Date of approval (MM/DD/YYYY)"
            name={`securityClearances[${index}].dateOfApproval`}
            required
          />
        </Paper>
      ))}
      <button
        className="button"
        onClick={() =>
          push({ federalAgency: "", clearanceLevel: "", dateOfApproval: "" })
        }
        type="button"
      >
        Add Record
      </button>
    </div>
  );
};

const CAGECodeFields = ({ form, push, remove }: any) => {
  const classes = useStyles();
  const values = getIn(form.values, "cageCodes");

  return (
    <div className={classes.root}>
      <FormLabel>
        7. Please provide below any additional CAGE Code(s) other than the code
        provided in Portal Section 2, question 9 that is associated with NIST
        800-171 compliance.
      </FormLabel>
      {values.map((_value: string, index: number) => (
        <Paper key={index} className={classes.paper} elevation={3}>
          <IconButton
            className={classes.closeButton}
            onClick={() => {
              remove(index);
              if (values.length <= 1) {
                push({
                  SSP: "",
                  supportedBySSP: "",
                  dateOfAssessment: "",
                  totalScore: "",
                  estimatedDateOfAchievement: "",
                });
              }
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>

          <YesNoField
            label="SSP"
            name={`cageCodes[${index}].SSP`}
            required={true}
          />
          <InputField
            label="CAGE Code Supported by SSP"
            name={`cageCodes[${index}].supportedBySSP`}
            required={true}
          />
          <InputField
            label="Date of Assessment (MM/DD/YYYY)"
            name={`cageCodes[${index}].dateOfAssessment`}
            required={true}
          />
          <InputField
            label="Total Score"
            name={`cageCodes[${index}].totalScore`}
            required={true}
          />
          <InputField
            label="Estimated Date a Score of 110 will be Achieved (MM/DD/YYYY)"
            name={`cageCodes[${index}].estimatedDateOfAchievement`}
            required={true}
          />
        </Paper>
      ))}
      <button
        className="button"
        onClick={() =>
          push({
            SSP: "",
            supportedBySSP: "",
            dateOfAssessment: "",
            totalScore: "",
            estimatedDateOfAchievement: "",
          })
        }
        type="button"
      >
        Add Record
      </button>
    </div>
  );
};

const SecurityAndCertificationsFields = () => {
  return (
    <FormStep>
      <FormSection title="M. Security and Cybersecurity">
        <YesNoField
          label="1. Does your company have a Facility Security Clearance?"
          name="facilitySecurityClearance"
        />
        <FieldArray
          component={SecurityClearancesFields}
          name="securityClearances"
        />
        <Details>
          <p>
            When a prime contract contains DFARS 252.204-7012 Safeguarding
            Covered Defense Information and Cyber Incident Reporting, the
            Subcontractor may be required to provide documentation of compliance
            with the National Institute of Standards and Technology Special
            Publication 800-171 (“NIST SP 800-171”). The information to be
            provided below is intended to determine if your company has
            implemented the security requirements of NIST SP 800-171 as a means
            to safeguard Department of Defense unclassified covered defense
            information that may be stored, transmitted or processed on your
            company information systems or networks. Non-compliance does not
            automatically disqualify a subcontractor for award.
          </p>
          <br />
          <p>
            For additional information regarding NIST SP 800-171 compliance,
            please review DFARS 252-204-7012 Safeguarding Covered Defense
            Information and Cyber Incident Reporting; 252.204-7019 Notice of
            NIST SP 800-171 DoD Assessment Requirements; and 252.204-7020 NIST
            SP 800-171 DoD Assessment Requirements.
          </p>
        </Details>

        <YesNoField
          label="3. Are your information systems NIST SP 800-171 compliant?"
          name="compliantIT"
          required={true}
        />
        <PleaseExplain name="compliantIT">
          <p>
            Please provide a copy of your compliance policy on the Signature page.
          </p>
        </PleaseExplain>

        <YesNoField
          label="4. Do you have a Security System Plan (SSP)? (Note: If “Yes” you will be asked to provide the SSP for file documentation)."
          name="canReviewSSP"
          required={true}
        />
        <PleaseExplain name="canReviewSSP">
          <p>
            Please provide a copy of your SSP File on the Signature page.
          </p>
        </PleaseExplain>
        <YesNoField
          label="5. Have you completed, within the last three years, a Basic Self NIST SP 800-171 Assessment which is posted on the Supplier Performance Risk Assessment System (SPRS)?"
          name="NISTSP800171Assessment"
          required={true}
        />
        <PleaseExplain name="NISTSP800171Assessment">
          <p>
            On the Signature page, please provide a screenshot of the SPRS registration.
            This will need to be a screen shot that shows POAM SSP uploaded, and your
            company's self assessment score. This screenshot is for verification of the
            information required below.
          </p>
          <YesNoField
            label="POAM SSP"
            name="poamSSP"
            required={true}
          />
          <InputField
            label="Company's Assessment Score"
            name="companyAssessmentScore"
            required={true}
          />
          <p>
            Please note your score as indicated below. If Score is below 110;
            please provide the date a score of 110 is expected.
          </p>
          <InputField
            label="Basic Self-Assessment Score"
            name="NISTSP800171AssessmentScore"
            required={false}
          />
          <InputField
            label="Date of Assessment (MM/DD/YYYY)"
            name="NISTSP800171AssessmentDate"
            required={false}
          />
          <InputField
            label="Date a Score of 110 is Expected (MM/DD/YYYY)"
            name="NISTSP800171AssessmentDateofScore110"
            required={false}
          />
        </PleaseExplain>

        <YesNoField
          label="6. Is your company Cybersecurity Maturity Model Certified?"
          name="CybersecurityMaturityModelCertified"
          required={true}
        />
        <PleaseExplain name="CybersecurityMaturityModelCertified">
          <p>If yes, please complete (a) and (b).</p>
          <InputField
            label="(a). Date of Assessment (MM/DD/YYYY)"
            name="CybersecurityMaturityModelCertifiedDate"
            required={true}
          />
          <InputField
            label="(b). CMMC Level"
            name="CybersecurityMaturityModelCertifiedCMMCLevel"
            required={true}
          />
        </PleaseExplain>
        <PleaseExplain name="CybersecurityMaturityModelCertified" value="False">
          <p>If response is “No” please complete (c).</p>
          <InputField
            label="(c). Date CMMC Passing Score is Expected"
            name="CybersecurityMaturityModelCertifiedCMMCPassingScore"
            required={true}
          />
        </PleaseExplain>
        <FieldArray component={CAGECodeFields} name="cageCodes" />
        <TextareaWithCharCount
          label="8. Notes"
          name="securityClearanceNotes"
          required={false}
          maxCharInput={1000}
        />
      </FormSection>
    </FormStep>
  );
};

export default SecurityAndCertificationsFields;
